// ============================================================================
// autoUpdate.ts — АВТОПРИМЕНЕНИЕ ОБНОВЛЕНИЯ ФРОНТА (PWA).
//
// Источник обнаружения — ТОТ ЖЕ, что у ручного баннера: стор
// useSwUpdateStore (swUpdate.notifyAppUpdated ставит флаг из SW-сообщения,
// checkBundleVersion и серверной версии). Новых источников обнаружения нет.
//
// Политика (decideAutoUpdate — чистая функция, покрыта тестами):
//   * свежая версия найдена → перезагружаем САМИ в безопасный момент:
//     приложение неактивно (вкладка скрыта или нет фокуса) ИЛИ прошёл короткий
//     таймаут (≤60с) — при этом НЕТ незавершённой отправки (очереди синка/чата,
//     идёт загрузка файла или запись голосового) и не включён «Автономный
//     режим»;
//   * иначе — ручной путь (баннер «Перезагрузить» показывает swUpdate) и
//     применяем обновление при следующем удобном случае;
//   * один перезапуск на обнаружение версии (анти-цикл: метка в
//     sessionStorage переживает reload — та же версия второй раз не
//     перезагружается автоматически).
// Перед reload дожидаемся активации ожидающего Service Worker
// (SKIP_WAITING → controllerchange, с таймаутом), затем reloadForUpdate()
// (учитывает запрет перезагрузки на desktop/Wails).
// ============================================================================
import { logSwallowed } from '@/lib/errors';
import { createLogger } from '@/lib/logger';

const log = createLogger('autoUpdate');

/** Короткий таймаут: через ≤60с обновление применяется даже при видимой вкладке. */
export const AUTO_UPDATE_TIMEOUT_MS = 60_000;
/** Страховочная перепроверка, пока обновление не применено (не поллинг данных). */
const FALLBACK_EVALUATE_MS = 15_000;
/** Ожидание активации ожидающего SW перед reload. */
const SW_ACTIVATION_WAIT_MS = 2_000;
/** Метка применённой версии (анти-цикл между reload). */
const RELOAD_MARK_KEY = 'hydra_auto_update_reloaded';

/** Источник занятости: запись голосового (ставит ChatThread). */
const VOICE_RECORDING_SOURCE = 'voice_recording';

export interface AutoUpdateInputs {
  /** Свежая версия найдена (SSoT — useSwUpdateStore). */
  updateAvailable: boolean;
  /** Уже перезагружались по ЭТОЙ версии в этой вкладке (анти-цикл). */
  alreadyApplied: boolean;
  /** «Автономный режим» — перезагрузка запрещена. */
  forcedOffline: boolean;
  /** Ожидают отправки в очереди синхронизации. */
  pendingSync: number;
  /** Ожидают отправки в очереди чата. */
  pendingChat: number;
  /** Идут/ждут передачи файлов. */
  activeUploads: number;
  /** Идёт запись голосового сообщения. */
  voiceRecording: boolean;
  /** Приложение неактивно: вкладка скрыта ИЛИ окно без фокуса. */
  inactive: boolean;
  /** Сколько миллисекунд прошло с момента обнаружения версии. */
  elapsedMs: number;
}

/** none — нечего делать; wait — ждём удобного момента; manual — только ручной путь; reload — применяем сами. */
export type AutoUpdateDecision = 'none' | 'wait' | 'manual' | 'reload';

/** Чистая политика автоприменения обновления (SSoT — здесь). */
export function decideAutoUpdate(input: AutoUpdateInputs): AutoUpdateDecision {
  if (!input.updateAvailable) return 'none';
  if (input.alreadyApplied) return 'none';
  if (input.forcedOffline) return 'manual';
  const busy =
    input.pendingSync > 0 ||
    input.pendingChat > 0 ||
    input.activeUploads > 0 ||
    input.voiceRecording;
  if (busy) return 'manual';
  if (input.inactive) return 'reload';
  if (input.elapsedMs >= AUTO_UPDATE_TIMEOUT_MS) return 'reload';
  return 'wait';
}

// ============================================================================
// Runtime (интеграция с приложением)
// ============================================================================

const busySources = new Set<string>();
let inited = false;
let reloadIssued = false;
let detectedAt = 0;
let reloadedVersion = '';
let blurred = false;
let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
let fallbackTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Занятость, которую не видят очереди (запись голосового). Источники ставят и
 * снимают отметку; снятие перепроверяет политику (обновление применится).
 */
export function setAutoUpdateBusy(source: string, busy: boolean): void {
  if (busy) busySources.add(source);
  else busySources.delete(source);
  if (!busy) void evaluate();
}

function readReloadMark(): string {
  try {
    return sessionStorage.getItem(RELOAD_MARK_KEY) || '';
  } catch {
    return '';
  }
}

function writeReloadMark(version: string): void {
  try {
    sessionStorage.setItem(RELOAD_MARK_KEY, version || '1');
  } catch {
    // приватный режим — метка не сохранится, цикл прервёт флаг reloadIssued
  }
}

function stopTimers(): void {
  if (deadlineTimer !== null) {
    clearTimeout(deadlineTimer);
    deadlineTimer = null;
  }
  if (fallbackTimer !== null) {
    clearInterval(fallbackTimer);
    fallbackTimer = null;
  }
}

function startTimers(): void {
  if (deadlineTimer === null) {
    deadlineTimer = setTimeout(() => {
      deadlineTimer = null;
      void evaluate();
    }, AUTO_UPDATE_TIMEOUT_MS);
  }
  if (fallbackTimer === null) {
    fallbackTimer = setInterval(() => {
      void evaluate();
    }, FALLBACK_EVALUATE_MS);
  }
}

function onInactive(): void {
  blurred = true;
  void evaluate();
}

/** Записи очередей недоступны — консервативно считаем, что отправка идёт. */
async function readBusyCounts(): Promise<{ pendingSync: number; pendingChat: number; activeUploads: number; failed: boolean }> {
  let pendingSync = 0;
  let pendingChat = 0;
  let activeUploads = 0;
  let failed = false;
  try {
    const { getPendingCount } = await import('@/lib/syncEngine');
    pendingSync = await getPendingCount();
  } catch (e) {
    failed = true;
    logSwallowed(e, 'lib/autoUpdate.ts:pendingSync');
  }
  try {
    const { pendingChatMessages } = await import('@/lib/chatQueue');
    pendingChat = await pendingChatMessages();
  } catch (e) {
    failed = true;
    logSwallowed(e, 'lib/autoUpdate.ts:pendingChat');
  }
  try {
    const { getUploadJobs } = await import('@/lib/chunkedUpload');
    activeUploads = getUploadJobs().filter(
      (j) => j.status === 'pending' || j.status === 'uploading' || j.status === 'paused' || j.status === 'queued',
    ).length;
  } catch (e) {
    failed = true;
    logSwallowed(e, 'lib/autoUpdate.ts:uploads');
  }
  return { pendingSync, pendingChat, activeUploads, failed };
}

/** Ожидание активации ожидающего SW (SKIP_WAITING → controllerchange) с таймаутом. */
async function activateWaitingServiceWorker(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg || !reg.waiting) return;
    reg.waiting.postMessage({ type: 'SKIP_WAITING' });
    await new Promise<void>((resolve) => {
      let done = false;
      const finish = (): void => {
        if (done) return;
        done = true;
        navigator.serviceWorker.removeEventListener('controllerchange', finish);
        resolve();
      };
      navigator.serviceWorker.addEventListener('controllerchange', finish);
      setTimeout(finish, SW_ACTIVATION_WAIT_MS);
    });
  } catch (e) {
    logSwallowed(e, 'lib/autoUpdate.ts:activateWaitingSw');
  }
}

/** Применяем обновление: метка анти-цикла → активация SW → перезагрузка. */
async function applyReload(version: string): Promise<void> {
  if (reloadIssued) return;
  reloadIssued = true;
  stopTimers();
  writeReloadMark(version);
  log.info('автоприменение обновления: ' + (version || 'версия неизвестна'));
  try {
    const { useToastStore } = await import('@/store/toastStore');
    const { translate } = await import('@/lib/i18n/store');
    useToastStore.getState().showInfo(translate('update.auto_applying'));
  } catch (e) {
    logSwallowed(e, 'lib/autoUpdate.ts:toast');
  }
  await activateWaitingServiceWorker();
  const { reloadForUpdate } = await import('@/lib/swUpdate');
  reloadForUpdate();
}

/** Перепроверка политики по актуальным данным. */
export async function evaluate(): Promise<void> {
  if (typeof document === 'undefined') return;
  if (reloadIssued) return;
  let updateAvailable = false;
  let version = '';
  try {
    const { useSwUpdateStore } = await import('@/lib/swUpdate');
    const st = useSwUpdateStore.getState();
    updateAvailable = st.updateAvailable;
    version = st.version;
  } catch (e) {
    logSwallowed(e, 'lib/autoUpdate.ts:store');
    return;
  }
  if (!updateAvailable) {
    stopTimers();
    return;
  }
  if (detectedAt === 0) detectedAt = Date.now();
  const { pendingSync, pendingChat, activeUploads, failed } = await readBusyCounts();
  let forcedOffline = false;
  try {
    const { isForcedOffline } = await import('@/lib/offlineFlag');
    forcedOffline = isForcedOffline();
  } catch (e) {
    logSwallowed(e, 'lib/autoUpdate.ts:offlineFlag');
  }
  const decision = decideAutoUpdate({
    updateAvailable,
    alreadyApplied: version !== '' && version === reloadedVersion,
    forcedOffline,
    // Сбой чтения очередей — консервативно «занято» (не перезагружаем).
    pendingSync: failed ? Math.max(pendingSync, 1) : pendingSync,
    pendingChat: failed ? Math.max(pendingChat, 1) : pendingChat,
    activeUploads: failed ? Math.max(activeUploads, 1) : activeUploads,
    voiceRecording: busySources.has(VOICE_RECORDING_SOURCE),
    inactive: document.hidden || blurred,
    elapsedMs: Date.now() - detectedAt,
  });
  if (decision === 'reload') {
    await applyReload(version);
  }
}

/**
 * Инициализация (main.tsx, после регистрации SW): подписка на обнаружение
 * версии (тот же SSoT, что у баннера) + события, которые делают момент
 * безопасным (скрытие вкладки/фокус, конец загрузок/очередей/записи,
 * снятие автономного режима) + короткий таймаут. Вызывается ОДИН раз.
 */
export function initAutoUpdate(): void {
  if (inited) return;
  inited = true;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  reloadedVersion = readReloadMark();
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) onInactive();
  });
  window.addEventListener('blur', onInactive);
  window.addEventListener('focus', () => {
    blurred = false;
  });
  void import('@/lib/swUpdate')
    .then(({ useSwUpdateStore }) => {
      useSwUpdateStore.subscribe((s, prev) => {
        if (s.updateAvailable && !prev.updateAvailable) {
          detectedAt = Date.now();
          startTimers();
          void evaluate();
        }
      });
      if (useSwUpdateStore.getState().updateAvailable) {
        detectedAt = Date.now();
        startTimers();
        void evaluate();
      }
    })
    .catch((e) => {
      logSwallowed(e, 'lib/autoUpdate.ts:initStore');
    });
  void import('@/lib/chunkedUpload')
    .then((m) => {
      m.onUploadsChange(() => {
        void evaluate();
      });
    })
    .catch((e) => {
      logSwallowed(e, 'lib/autoUpdate.ts:initUploads');
    });
  void import('@/lib/chatQueue')
    .then((m) => {
      m.useChatQueueStore.subscribe(() => {
        void evaluate();
      });
    })
    .catch((e) => {
      logSwallowed(e, 'lib/autoUpdate.ts:initChatQueue');
    });
  void import('@/lib/syncEngine')
    .then((m) => {
      m.syncEngine.onStatusChange(() => {
        void evaluate();
      });
    })
    .catch((e) => {
      logSwallowed(e, 'lib/autoUpdate.ts:initSync');
    });
  void import('@/lib/offlineFlag')
    .then((m) => {
      m.subscribeOfflineFlag(() => {
        void evaluate();
      });
    })
    .catch((e) => {
      logSwallowed(e, 'lib/autoUpdate.ts:initOfflineFlag');
    });
}
